import * as DiceConstants from "@/game/constants/diceConstants";
import type { GameBoardOverlayInterface } from "@/game/context/GameBoardOverlayContext.ts";
import { useDiceRollInitial } from "@/game/hooks/useDiceRollInitial.ts";
import { useDiceRollInterstitial } from "@/game/hooks/useDiceRollInterstitial.ts";
import type { DieState } from "@/game/hooks/useGamePlayState.ts";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import type { PlayerColor } from "@nextgammon/shared";
import { Constants } from "@nextgammon/shared";
import { useMemo } from "react";

import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";

interface GameDiceProps {
  currentDice: DieState[] | undefined;
  whiteDice: DieState[] | undefined;
  blackDice: DieState[] | undefined;
  diceRolled: boolean;
  playerColor: PlayerColor;
  currentPlayerColor: PlayerColor;
  playerUsername?: string;
  opponentUsername?: string;
  diceArea: [number, number, number, number];
  playArea: [number, number, number, number];
  boardScale: number;
  shouldShowRollInitial: boolean;
  shouldShowRollInterstitial: boolean;
  overlayInterface: GameBoardOverlayInterface | null;
  onRollAnimationComplete: () => void;
  gameMode: string; // NEW: Game mode for animation timing
}

export function GameDice({
  currentDice,
  whiteDice,
  blackDice,
  diceRolled,
  playerColor,
  currentPlayerColor,
  playerUsername,
  opponentUsername,
  diceArea,
  playArea,
  boardScale,
  shouldShowRollInitial,
  shouldShowRollInterstitial,
  overlayInterface,
  onRollAnimationComplete,
  gameMode
}: GameDiceProps) {
  // Load the player dice textures
  const playerTextures = useMemo(() => {
    return Array.from({ length: 6 }, (_, index) => PIXI.Assets.get(`dice-local-${index + 1}`));
  }, []);

  // Load the opponent dice textures
  const opponentTextures = useMemo(() => {
    return Array.from({ length: 6 }, (_, index) => PIXI.Assets.get(`dice-opponent-${index + 1}`));
  }, []);

  const diceScale = boardScale * DiceConstants.DICE_IMAGE_SCALE;

  const isPlayerTurn = playerColor === currentPlayerColor;
  let textures = isPlayerTurn ? playerTextures : opponentTextures;
  let dice = currentPlayerColor === Constants.PLAYER_WHITE ? whiteDice : blackDice;

  // Memoize dice values to prevent unnecessary re-renders
  // Use JSON.stringify to create a stable key based on actual values, not array reference
  const currentDiceKey = currentDice
    ? JSON.stringify(currentDice.map((d) => d.value).sort())
    : "[]";
  const initialDiceValues = useMemo(
    () => (currentDice ? currentDice.map((die) => die.value) : []),
    [currentDiceKey]
  );

  // Handle the initial roll animation overlay if needed
  const { isInitialRollInProgress } = useDiceRollInitial({
    initialDice: initialDiceValues,
    playerTextures,
    opponentTextures,
    playerColor,
    currentPlayerColor,
    playerUsername,
    opponentUsername,
    diceArea,
    playArea,
    diceScale,
    boardScale: boardScale,
    shouldShowRoll: shouldShowRollInitial,
    overlayInterface,
    onRollAnimationComplete,
    gameMode
  });

  // Memoize interstitial dice values to prevent unnecessary re-renders
  // Use JSON.stringify to create a stable key based on actual values, not array reference
  const diceKey = dice ? JSON.stringify(dice.map((d) => d.value).sort()) : "[]";
  const interstitialDiceValues = useMemo(
    () => (dice ? dice.map((die) => die.value) : []),
    [diceKey]
  );

  // Handle the interstitial roll animation overlay if needed
  const { isInterstitialRollInProgress } = useDiceRollInterstitial({
    diceValues: interstitialDiceValues,
    diceTextures: textures,
    shouldShowRoll: shouldShowRollInterstitial,
    diceArea,
    playArea,
    diceScale,
    boardScale,
    isPlayerTurn,
    playerUsername,
    opponentUsername,
    overlayInterface,
    onRollAnimationComplete,
    gameMode
  });

  // If the dice for the current player have not been rolled, attempt to render the dice from the previous turn
  if (!diceRolled) {
    textures = isPlayerTurn ? opponentTextures : playerTextures;
    dice = currentPlayerColor === Constants.PLAYER_WHITE ? blackDice : whiteDice;
  }

  if (!dice || dice.length === 0 || !textures || textures.length < 6) {
    GameLogger.logInfo(
      "GameDice",
      `No dice to render (${dice == undefined || dice.length === 0}) or textures missing (${!textures || textures.length < 6})`
    );
    return null;
  }

  if (
    shouldShowRollInitial ||
    shouldShowRollInterstitial ||
    isInitialRollInProgress ||
    isInterstitialRollInProgress
  ) {
    GameLogger.logInfo(
      "GameDice",
      `Roll animation is requested (initial=${shouldShowRollInitial}) (interstitial=${shouldShowRollInterstitial}) or animation not completed (initial=${isInitialRollInProgress}) (interstitial=${isInterstitialRollInProgress})`
    );
    return null;
  }

  return (
    <>
      {dice.map((die, idx) => {
        // Validate die value is within expected range
        if (!die || typeof die.value !== "number" || die.value < 1 || die.value > 6) {
          GameLogger.logError("GameDice", `Invalid die value at index ${idx}:`, die);
          return null;
        }

        const position: [number, number] = [
          diceArea[0] + diceArea[2] / 2,
          diceArea[1] + (diceArea[3] / 4) * idx
        ];

        const textureIndex = die.value - 1;
        const texture = textures[textureIndex];

        if (!texture) {
          GameLogger.logError(
            "GameDice",
            `Missing texture for die value ${die.value} at index ${textureIndex}`
          );
          return null;
        }

        return (
          <Sprite
            key={idx}
            texture={texture}
            anchor={[0.5, 0]}
            position={position}
            scale={[diceScale, diceScale]}
            roundPixels={true}
            alpha={die.used || !diceRolled ? 0.5 : 1}
          />
        );
      })}
    </>
  );
}
